'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { resolveApiErrorToastMessageOrFallback } from '../lib/api/localizeApiError';
import type { NotificationSettings, NotificationSettingsPatch } from '../lib/api';
import {
  useNotificationSettings,
  useUpdateNotificationSettingsMutation,
} from '../hooks/queries/useNotificationSettings';
import { cn } from '../lib/cn';
import { Select, type SelectOption } from './Select';
import { Spinner } from './loading/Spinner';

// 계정 전역 알림 규칙(모든 프로젝트 공통). 프로젝트 구독 상태와 무관하게 조작 가능해야 하므로
// projectId를 받지 않고, 프로젝트 설정이 아니라 프로필 설정에서 노출한다.
// 서버 기본값과 동일하게 두어, 로딩 중에도 화면이 흔들리지 않게 한다.
const defaultNotificationSettings: NotificationSettings = {
  scopeMode: 'ALL',
  workScheduleEnabled: false,
  workDays: [1, 2, 3, 4, 5],
  startMinute: 540,
  endMinute: 1080,
  timezone: 'UTC',
};

// 표시 순서는 월~일, 값은 서버 규약(0=일 ~ 6=토)을 따른다.
const workDayOptions: ReadonlyArray<{ value: number; labelKey: string }> = [
  { value: 1, labelKey: 'notificationPersonalRules.weekdayMon' },
  { value: 2, labelKey: 'notificationPersonalRules.weekdayTue' },
  { value: 3, labelKey: 'notificationPersonalRules.weekdayWed' },
  { value: 4, labelKey: 'notificationPersonalRules.weekdayThu' },
  { value: 5, labelKey: 'notificationPersonalRules.weekdayFri' },
  { value: 6, labelKey: 'notificationPersonalRules.weekdaySat' },
  { value: 0, labelKey: 'notificationPersonalRules.weekdaySun' },
];

const minutesInDay = 24 * 60;
const timeStepMinutes = 30;

const formatMinuteOfDay = (minute: number) => {
  const normalized = Math.max(0, Math.min(minutesInDay - 1, minute));
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

// 30분 단위 옵션. 저장된 값이 30분 배수가 아니어도 선택 상태를 잃지 않도록 현재 값을 합쳐 준다.
const buildTimeOptions = (currentMinute: number): SelectOption[] => {
  const minutes: number[] = [];
  for (let minute = 0; minute < minutesInDay; minute += timeStepMinutes) {
    minutes.push(minute);
  }
  if (!minutes.includes(currentMinute)) {
    minutes.push(currentMinute);
    minutes.sort((a, b) => a - b);
  }
  return minutes.map((minute) => ({ value: String(minute), label: formatMinuteOfDay(minute) }));
};

const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

type PersonalNotificationRulesProps = {
  token: string | null;
};

export default function PersonalNotificationRules({ token }: PersonalNotificationRulesProps) {
  const t = useTranslations('profile.page');
  const tRoot = useTranslations();

  const settingsQuery = useNotificationSettings(token);
  const updateSettingsMutation = useUpdateNotificationSettingsMutation(token);
  const { mutateAsync: mutateSettings } = updateSettingsMutation;

  const settings = settingsQuery.data ?? defaultNotificationSettings;
  const storedTimezone = settingsQuery.data?.timezone ?? null;
  const hasSyncedTimezoneRef = useRef(false);

  // 서버는 저장된 timezone 기준으로 발송 시각을 계산하므로, 브라우저가 알려주는 실제 지역과
  // 다르면 조용히 맞춰 준다. 값이 같으면 PUT 하지 않아야 재요청 루프가 생기지 않는다.
  useEffect(() => {
    if (!storedTimezone || hasSyncedTimezoneRef.current) {
      return;
    }

    hasSyncedTimezoneRef.current = true;

    const browserTimezone = getBrowserTimezone();
    if (!browserTimezone || browserTimezone === storedTimezone) {
      return;
    }

    void mutateSettings({ timezone: browserTimezone }).catch(() => undefined);
  }, [mutateSettings, storedTimezone]);

  const save = async (patch: NotificationSettingsPatch) => {
    try {
      await mutateSettings(patch);
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('notificationPersonalRules.updateFailed'));
      if (message) toast.error(message);
    }
  };

  const handleToggleWorkDay = (day: number) => {
    const next = settings.workDays.includes(day)
      ? settings.workDays.filter((value) => value !== day)
      : [...settings.workDays, day].sort((a, b) => a - b);
    void save({ workDays: next });
  };

  const startTimeOptions = useMemo(() => buildTimeOptions(settings.startMinute), [settings.startMinute]);
  const endTimeOptions = useMemo(() => buildTimeOptions(settings.endMinute), [settings.endMinute]);
  const effectiveTimezone = storedTimezone ?? getBrowserTimezone() ?? defaultNotificationSettings.timezone;

  return (
    <div>
      <div className="flex min-w-0 items-start justify-between gap-2">
        <p className="min-w-0 text-xs text-[var(--color-fg-muted)]">{t('notificationPersonalRules.description')}</p>
        {settingsQuery.isFetching ? <Spinner /> : null}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold">{t('notificationPersonalRules.scopePersonalLabel')}</h4>
          <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
            {t('notificationPersonalRules.scopePersonalDescription')}
          </p>
        </div>
        <input
          type="checkbox"
          role="switch"
          aria-label={t('notificationPersonalRules.scopePersonalLabel')}
          className="toggle toggle-primary shrink-0"
          checked={settings.scopeMode === 'PERSONAL'}
          onChange={(event) => {
            void save({ scopeMode: event.target.checked ? 'PERSONAL' : 'ALL' });
          }}
        />
      </div>

      <div className="mt-6 border-t border-base-300 pt-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h4 className="text-sm font-semibold">{t('notificationPersonalRules.workScheduleLabel')}</h4>
            <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
              {t('notificationPersonalRules.workScheduleDescription')}
            </p>
          </div>
          <input
            type="checkbox"
            role="switch"
            aria-label={t('notificationPersonalRules.workScheduleLabel')}
            className="toggle toggle-primary shrink-0"
            checked={settings.workScheduleEnabled}
            onChange={(event) => {
              void save({ workScheduleEnabled: event.target.checked });
            }}
          />
        </div>

        {settings.workScheduleEnabled ? (
          <div className="mt-3 space-y-3">
            <div>
              <p className="text-xs font-medium text-[var(--color-fg-subtle)]">
                {t('notificationPersonalRules.workDaysLabel')}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {workDayOptions.map((option) => {
                  const isSelected = settings.workDays.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={isSelected}
                      className={cn(
                        'h-8 w-8 rounded-full border text-xs font-medium transition',
                        isSelected
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-base-300 text-[var(--color-fg-muted)] hover:border-base-content/30',
                      )}
                      onClick={() => {
                        handleToggleWorkDay(option.value);
                      }}
                    >
                      {t(option.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <label className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-[var(--color-fg-subtle)]">
                  {t('notificationPersonalRules.startTimeLabel')}
                </span>
                <Select
                  className="mt-1.5"
                  fullWidth
                  size="sm"
                  options={startTimeOptions}
                  value={String(settings.startMinute)}
                  onChange={(value) => {
                    void save({ startMinute: Number(value) });
                  }}
                />
              </label>
              <label className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-[var(--color-fg-subtle)]">
                  {t('notificationPersonalRules.endTimeLabel')}
                </span>
                <Select
                  className="mt-1.5"
                  fullWidth
                  size="sm"
                  options={endTimeOptions}
                  value={String(settings.endMinute)}
                  onChange={(value) => {
                    void save({ endMinute: Number(value) });
                  }}
                />
              </label>
            </div>

            <p className="text-xs text-[var(--color-fg-muted)]">
              {t('notificationPersonalRules.timezoneHelper', { timezone: effectiveTimezone })}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
